import Vue from 'vue';
import {cloneDeep} from 'lodash';

export const BusinessType = {
  LEGAL: 'legal',
  INDIVIDUAL: 'individual',
  ENTREPRENEUR: 'entrepreneur'
};

export const businessTypeOptions = [
  { value: BusinessType.LEGAL, text: 'Legal Entity'},
  { value: BusinessType.INDIVIDUAL, text: 'Individual'},
  { value: BusinessType.ENTREPRENEUR, text: 'Individual Entrepreneur'},
];

export const businessTypeValues = Object.fromEntries(businessTypeOptions.map(type => [type.value, type.text]));

export const TaxIdentifier = {
  VAT: 'vat',
  TAX_NUMBER: 'taxNumber'
}

export const taxIdentifierOptions = [
  { value: TaxIdentifier.VAT, text: 'VAT'},
  { value: TaxIdentifier.TAX_NUMBER, text: 'Tax Number'},
];

export const Validation = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  COMPANY: 'company',
  EMAIL: 'email',
  COUNTRY: 'country',
  STATE: 'state',
  CITY: 'city',
  ADDRESS: 'address',
  ZIP_CODE: 'zipCode',
  TAX: 'tax'
};

const nameRules = {required: true, min: 2, max: 100, regex: /^(?=.*[a-z])[a-z' -]+$/i};
const geoRules = {required: true};

export const validationRules = {
  [Validation.FIRST_NAME]: nameRules,
  [Validation.LAST_NAME]: nameRules,
  [Validation.COMPANY]: {required: true, min: 2, max: 100, regex: /^(?=.*[a-z])[a-z0-9 ,.\-_/+&()']+$/i},
  [Validation.EMAIL]: {required: true, email: true},
  [Validation.COUNTRY]: geoRules,
  [Validation.CITY]: geoRules,
  [Validation.ADDRESS]: {required: true, min: 2, max: 255, regex: /^(?=.*[a-z0-9])[a-z0-9 ,.\-_/+#():&']+$/i},
  [Validation.ZIP_CODE]: {required: true, regex: /^[a-z0-9][a-z0-9\- ]{0,10}[a-z0-9]$/i},
  [Validation.TAX]: {regex: /^[A-Z0-9][A-Z0-9 .-]{4,28}[A-Z0-9]$/i},

  /**
   * @param {boolean} isRequired
   *
   * @return {{required: boolean}}
   */
  [Validation.STATE]: (isRequired) => ({required: isRequired}),
};

export const defaultModel = {
  businessType: null,
  firstName: null,
  lastName: null,
  company: null,
  email: null,
  country: null,
  state: null,
  city: null,
  address: null,
  zipCode: null,
  taxIdentifierType: null,
  taxIdentifierNumber: null
};

function getBeneficiaryPayload(model) {
  const params = cloneDeep(model);

  if (model.businessType === BusinessType.LEGAL) {
    params.firstName = null;
    params.lastName = null;
  } else {
    params.company = null;
  }

  if (model.businessType === BusinessType.INDIVIDUAL || !model.taxIdentifierType || !model.taxIdentifierNumber) {
    params.taxIdentifierType = null;
    params.taxIdentifierNumber = null;
  }

  return params;
}


/**
 * @typedef {Object<string, string|number>} BeneficiaryInfo
 * @property {BusinessType} businessType
 * @property {string|null} firstName
 * @property {string|null} lastName
 * @property {string|null} company
 * @property {string} email
 * @property {string} country
 * @property {string|null} state
 * @property {string} city
 * @property {string} address
 * @property {string} zipCode
 * @property {string} taxIdentifierType
 * @property {string} taxIdentifierNumber
 */
/**
 * @typedef {Object<string, string|number>} DefaultResponse
 * @property {number} id
 * @property {BeneficiaryInfo} data
 */
export class BillingBeneficiaryService {
  constructor() {
    this._url = '/v2/billing/beneficiaries';
    this._axios = Vue.prototype.$axios;
  }

  set axios(value) {
    this._axios = value;
  }

  /**
   * Get a billing beneficiary.
   *
   * @return {Promise<{data: DefaultResponse[]}>}
   */
  async all() {
    const response = await this._axios.get(`${this._url}`);
    return response.data;
  }

  /**
   * Store or update a billing beneficiary.
   *
   * @param {BeneficiaryInfo} params
   * @param {number|null} id
   *
   * @return {Promise<DefaultResponse>}
   */
  async save(params, id) {
    const payload = getBeneficiaryPayload(params);
    try {
      const response = id
        ? await this._axios.patch(`${this._url}/${id}`, payload)
        : await this._axios.post(this._url, payload);
      return response.data.data;
    } catch (error) {
      this.notifyValidationErrors(error);
      throw error;
    }
  }

  /**
   * Delete a billing beneficiary.
   *
   * @param {number} id
   *
   * @return {Promise<DefaultResponse>}
   */
  async delete(id) {
    try {
      const response = await this._axios.delete(`${this._url}/${id}`);
      return response.data;
    } catch (error) {
      this.notifyValidationErrors(error);
      throw error;
    }
  }

  notifyValidationErrors(error) {
    if (!error.response || error.response.status !== 422) {
      return;
    }

    const {errors, message} = error.response.data || {};
    const messages = [].concat(...Object.values(errors || {}))
      .filter(value => typeof value === 'string' && value.trim());

    if (!messages.length) {
      messages.push(message || 'Please check the beneficiary information and try again.');
    }

    [...new Set(messages)].forEach(text => Vue.prototype.$notify.error(text));
  }
}
